import * as React from 'react';
import * as ContractRepository from "../../../Repository/ContractRepository";
import { UserRoles } from "../../../Repository/UserRepository";
import { isAllowed } from "../../../Service/AuthService";
import * as TreeRepository from "../../../Repository/TreeRepository";
import message from "antd/es/message";
import { Button, Popconfirm } from "antd";
import { Contract, Tree, User } from "tree-wallet-client";

interface MapPopupDeleteButtonProps {
  isAdmin: boolean;
  isValidated: boolean;
  loading: boolean;
  user: User;
  tree: Tree;
  contract: Contract;
  setLoading: Function;
  reloadTrees: Function;
  setOpenedPopup: Function;
  /** Set while a validation is being filled in: that form owns the window, so Delete button is disabled */
  validationInProgress?: boolean;
  /** Told while the delete action is busy, the Validate button beside it is disabled */
  onBusyChange?: (busy: boolean) => void;
}

// During a failure API's own message is a status code plus a server sentence:
// a refusal for rights (401/403) arrives as a `TokenExpiredException`, hence the
// name test; anything else (a 500, a validation blocking the deletion...) is a plain failure
function describeDeleteFailure(error: any): string {
  const status = error && error.status;
  const refused = status === 401 || status === 403 || (error && error.name === 'TokenExpiredException');

  return refused ? 'No permission to delete this tree' : 'The tree could not be deleted';
}

export default function MapPopupDeleteButton(props: MapPopupDeleteButtonProps) {
  const { user, contract, reloadTrees, setOpenedPopup, tree, loading, validationInProgress, onBusyChange } = props;

  // This popup is rendered by `TreeMaps` into a React root of its own, detached from the app tree: the
  // global context is not reachable here (`useContext` answers with its defaults) and the app's online
  // status would not re-render it either. The browser's own events are followed instead.
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  React.useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    addEventListener('online', updateOnlineStatus);
    addEventListener('offline', updateOnlineStatus);
    return () => {
      removeEventListener('online', updateOnlineStatus);
      removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // Set while the deletion is on its way to the server: the confirmation then stays open,
  // with text "Yes" replaced by a rotating spinner
  const [deleting, setDeleting] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // The row is busy from the moment the delete confirmation opens, and it stays busy once a deletion is under way,
  // so Validate button is disabled until deletion is aborted or completed when tree popup closes
  React.useEffect(() => {
    onBusyChange && onBusyChange(confirmOpen || deleting);
  }, [confirmOpen, deleting, onBusyChange]);

  // Check if the tree has been validated at least once
  const hasBeenValidated = (tree.treeValidations?.length ?? 0) > 0;

  const shouldDisplayDeleteButton = () => {
    const roles = [
      ContractRepository.roles.TreePlanter,
      ContractRepository.roles.LandOwner,
      UserRoles.CompensationProgram,
      UserRoles.Admin,
      ContractRepository.roles.Coordinator,

    ];

    // Offline, and for a tree that has already been validated once, the button stays in the row and is
    // disabled below rather than hidden.
    return user
      && contract
      && isAllowed([...user.roles, ...(contract.roles ? contract.roles : [])], roles);
  }

  const onDelete = () => {
    setDeleting(true);

    // Nothing is withdrawn from the map or counted down until the server has accepted the deletion: 
    // a refusal then leaves the tree exactly where it was, with nothing to undo (see the error callback).
    TreeRepository.deleteTree(tree, {
      successCallback: () => {
        // 1. Accepted: the map is told, and it is the one that finishes the deletion - the icon goes once
        // this window is over, and it is not clickable until then. 2. The contract's count is asked there
        // too, once the icon is gone.
        reloadTrees(tree.id, tree.numberPlanted * -1);
        setOpenedPopup(false);
      },
      errorCallback: (error: any) => {
        // The tree was never touched: the operation is abandoned, and the confirmation goes back to its
        // initial state so the user can retry
        message.error(describeDeleteFailure(error));
        setDeleting(false);
      }
    }).catch((error) => {
      console.error('Delete call failed', error);
      setDeleting(false);
    });
  };

  if (!shouldDisplayDeleteButton()) {
    return <></>
  }

  // Check if delete button should be locked
  const deleteIsLocked = !isOnline || hasBeenValidated || !!validationInProgress;

  const deleteButton = (
    <Button type="primary" danger loading={loading} disabled={deleteIsLocked}>Delete</Button>
  );

  return <Popconfirm
    // Left-aligned on the button, not centred: a centred box is half its width to the left of a button that
    // sits near the left edge of the window, which is exactly what pushed it outside the tree popup on a
    // phone. This way it grows to the right, inside the window, whatever the screen on Mobile and Desktop
    placement="topLeft"
    // Sized and spaced in `assets/less/map.less`, under this class.
    overlayClassName="tree-delete-confirmation"
    // Delete confirmation belongs to the window that asked for it, and is drawn inside it rather than at body
    // level (anchored to the popup's own window, a width in % is the window's, which is what keeps the box
    // inside it on any screen - see the class below)
    getPopupContainer={(triggerNode: HTMLElement) => (triggerNode.closest('.ant-modal-content') as HTMLElement) || document.body}
    title={'Do you really want to delete this tree ?'}
    open={confirmOpen}
    // A locked Delete button must not open the delete confirmation. antd wraps a disabled child in a span of its own
    // - so that a tooltip still shows on it - and that span takes the click, so `disabled` alone was not enough
    onOpenChange={(nextOpen: boolean) => { if (deleting || deleteIsLocked) return; setConfirmOpen(nextOpen); }}
    onConfirm={onDelete}
    // The text label 'Yes' is replaced by the spinner when deleting, avoiding the user to click it again
    okText={deleting ? '' : 'Yes'}
    okButtonProps={{ loading: deleting }}
    cancelText="No"
    cancelButtonProps={{ disabled: deleting }}
  >
    {/* Only the Admin can delete a validated tree */}
    {deleteButton}
  </Popconfirm>
}